// The legal documents' Markdown, drawn as DOM. What Markdig drew for the Blazor dialog: headings, paragraphs, lists,
// tables, emphasis, links, quotes and rules. Every piece of text goes in as a text node and only http(s) and mailto
// links are links, so raw HTML pasted into a document - out of a lawyer's Word export, say - stays text, as
// Markdig's DisableHtml kept it.

import { h } from './dom.ts';

const SAFE_LINK = /^(https?:|mailto:)/i;

/** **bold**, *italic*, `code`, [text](url), inline. */
function inline(text: string): Array<Node | string> {
    const nodes: Array<Node | string> = [];
    const pattern = /(\*\*|__)(.+?)\1|(\*|_)(?!\s)(.+?)\3|`([^`]+)`|\[([^\]]+)\]\(([^)\s]+)(?:\s+"[^"]*")?\)/g;
    let last = 0;
    let match: RegExpExecArray | null;

    while ((match = pattern.exec(text))) {
        if (match.index > last) nodes.push(text.slice(last, match.index));

        if (match[2] !== undefined) {
            nodes.push(h('strong', null, inline(match[2])));
        } else if (match[4] !== undefined) {
            nodes.push(h('em', null, inline(match[4])));
        } else if (match[5] !== undefined) {
            nodes.push(h('code', null, match[5]));
        } else {
            const href = match[7]!;

            nodes.push(SAFE_LINK.test(href)
                ? h('a', { href, target: '_blank', rel: 'noopener noreferrer' }, inline(match[6]!))
                : match[0]);
        }

        last = pattern.lastIndex;
    }

    if (last < text.length) nodes.push(text.slice(last));

    return nodes;
}

const isTableDivider = (line: string): boolean => /^\s*\|?\s*:?-{2,}:?\s*(\|\s*:?-{2,}:?\s*)*\|?\s*$/.test(line);

const cells = (line: string): string[] => line.trim().replace(/^\|/, '').replace(/\|$/, '').split('|').map(cell => cell.trim());

const LIST_ITEM = /^(\s*)([-*+]|\d+[.)])\s+(.*)$/;

/** A run of list lines, nested by indentation. */
function list(lines: string[], start: number): [HTMLElement, number] {
    const first = lines[start]!.match(LIST_ITEM)!;
    const indent = first[1]!.length;
    const ordered = /\d/.test(first[2]!);
    const element = h(ordered ? 'ol' : 'ul');
    let i = start;
    let item: HTMLElement | null = null;

    while (i < lines.length) {
        const match = lines[i].match(LIST_ITEM);

        if (match && match[1].length === indent) {
            item = h('li', null, inline(match[3]));
            element.appendChild(item);
            i++;
        } else if (match && match[1].length > indent && item) {
            const [nested, next] = list(lines, i);

            item.appendChild(nested);
            i = next;
        } else if (!match && lines[i].trim() && item && /^\s+/.test(lines[i])) {
            item.append(' ', ...inline(lines[i].trim()));
            i++;
        } else {
            break;
        }
    }

    return [element, i];
}

export function renderMarkdown(markdown: string): Node[] {
    const lines = (markdown || '').replace(/\r\n?/g, '\n').split('\n');
    const blocks: Node[] = [];
    let i = 0;

    while (i < lines.length) {
        const line = lines[i];

        if (!line.trim()) {
            i++;
            continue;
        }

        const heading = line.match(/^(#{1,6})\s+(.*?)\s*#*\s*$/);

        if (heading) {
            blocks.push(h(`h${heading[1].length}`, null, inline(heading[2])));
            i++;
            continue;
        }

        if (/^\s*([-*_])(\s*\1){2,}\s*$/.test(line)) {
            blocks.push(h('hr'));
            i++;
            continue;
        }

        if (line.includes('|') && i + 1 < lines.length && isTableDivider(lines[i + 1])) {
            const head = cells(line);
            const rows: string[][] = [];

            i += 2;

            while (i < lines.length && lines[i].includes('|') && lines[i].trim()) {
                rows.push(cells(lines[i]));
                i++;
            }

            blocks.push(h('table', null,
                h('thead', null, h('tr', null, head.map(cell => h('th', null, inline(cell))))),
                h('tbody', null, rows.map(row => h('tr', null, row.map(cell => h('td', null, inline(cell))))))));
            continue;
        }

        if (LIST_ITEM.test(line)) {
            const [element, next] = list(lines, i);

            blocks.push(element);
            i = next;
            continue;
        }

        if (/^\s*>/.test(line)) {
            const quoted: string[] = [];

            while (i < lines.length && /^\s*>/.test(lines[i])) {
                quoted.push(lines[i].replace(/^\s*>\s?/, ''));
                i++;
            }

            blocks.push(h('blockquote', null, renderMarkdown(quoted.join('\n'))));
            continue;
        }

        // A paragraph runs until a blank line or the start of another block; a line ending in two spaces breaks.
        const paragraph: string[] = [];

        while (i < lines.length && lines[i].trim()
            && !/^(#{1,6})\s/.test(lines[i]) && !LIST_ITEM.test(lines[i]) && !/^\s*>/.test(lines[i])
            && !(lines[i].includes('|') && i + 1 < lines.length && isTableDivider(lines[i + 1]))) {
            paragraph.push(lines[i]);
            i++;
        }

        const content: Array<Node | string> = [];

        paragraph.forEach((text, index) => {
            content.push(...inline(text.trim()));
            if (index < paragraph.length - 1) content.push(/ {2,}$/.test(text) ? h('br') : ' ');
        });

        blocks.push(h('p', null, content));
    }

    return blocks;
}
